import { useState } from 'react';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  ItemCardGrid,
  ItemCardHeader,
  Link,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { PsDocCategory, PsDocLink, psDocCategories } from './psDocLinks';

type ViewMode = 'tiles' | 'list';

/**
 * Tile view for the documents in one category. External content (Confluence,
 * Google Drive) opens in a new tab — it is linked as a destination, never
 * rendered inside Backstage.
 */
const PsDocLinkGrid = ({ links }: { links: PsDocLink[] }) => (
  <ItemCardGrid>
    {links.map(link => (
      <Card key={`${link.title}:${link.url}`}>
        <CardActionArea
          component="a"
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ItemCardHeader title={link.title} />
          <CardContent>
            <Typography variant="body2" paragraph>
              {link.description}
            </Typography>
            <Chip label={link.sourceType} size="small" />
          </CardContent>
        </CardActionArea>
      </Card>
    ))}
  </ItemCardGrid>
);

const linkColumns: TableColumn<PsDocLink>[] = [
  {
    title: 'Document',
    field: 'title',
    highlight: true,
    render: link => (
      <Link to={link.url} target="_blank" rel="noopener noreferrer">
        {link.title}
      </Link>
    ),
  },
  { title: 'Description', field: 'description' },
  { title: 'Source', field: 'sourceType' },
];

/** List view counterpart of PsDocLinkGrid. */
const PsDocLinkTable = ({
  links,
  title,
}: {
  links: PsDocLink[];
  title: string;
}) => (
  <Table<PsDocLink>
    title={title}
    options={{ paging: false, search: true }}
    columns={linkColumns}
    data={links}
  />
);

/**
 * Professional Services docs explorer: one tile per category (PS Home,
 * Training, Workshops, How-To, Onboarding); selecting a category shows its
 * documents in the requested view mode with a back button.
 */
export const PsDocsExplorer = ({ view }: { view: ViewMode }) => {
  const [category, setCategory] = useState<PsDocCategory | undefined>();

  if (!category) {
    return (
      <ItemCardGrid>
        {psDocCategories.map(c => (
          <Card key={c.title}>
            <CardActionArea onClick={() => setCategory(c)}>
              <ItemCardHeader title={c.title} />
              <CardContent>
                <Typography variant="body2" paragraph>
                  {c.description}
                </Typography>
                <Chip
                  label={`${c.links.length} document${
                    c.links.length === 1 ? '' : 's'
                  }`}
                  size="small"
                />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </ItemCardGrid>
    );
  }

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => setCategory(undefined)}
      >
        All categories
      </Button>
      <Typography variant="h5" gutterBottom>
        {category.title}
      </Typography>
      <Typography variant="body1" paragraph>
        {category.description}
      </Typography>
      {view === 'tiles' ? (
        <PsDocLinkGrid links={category.links} />
      ) : (
        <PsDocLinkTable links={category.links} title={category.title} />
      )}
    </>
  );
};
